import { HttpClient, HttpParams } from '@angular/common/http';
import moment from 'moment';
import { catchError, forkJoin, map, Observable, of, switchMap } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  IPratica,
  Pratica,
  PraticaSearchDTO,
  Target,
  StatoPratica,
  PraticaType,
} from '../../../interfaces/pratica';
import { PraticaAggregator } from '../../settings/header-requests/pratica-aggregator';
import { PraticaChannelConfigurationDialogReturnData } from '../../settings/header-requests/pratica-channel-configuration';
import { typedEntries } from '../../_utils/typed-entries';
import { genHttpParams } from '../../_utils/utils';
import { ProductType } from '../../requests/models/product';

export class PraticaService {
  private readonly queryEndpoint = `${environment.api}/pratica-query/pratica`;
  private readonly commandEndpoint = `${environment.api}/pratica-command/pratica`;
  private readonly aggregatorEndpoint = `${environment.api}/aggregatore-pratica/praticaAggregator`;
  private readonly processEndpoint = `${environment.api}/camunda/api/v1/process/nuova-richiesta/start`;
  private readonly istruttoriaEndpoint = `${environment.api}/camunda/api/v1/process/istruttoria`;

  constructor(private http: HttpClient) {}

  edit(id: string, updatedData: Partial<IPratica>) {
    return this.http.patch(`${this.commandEndpoint}/${id}`, updatedData);
  }

  getById(id: string) {
    return this.http.get<IPratica>(`${this.aggregatorEndpoint}/pratica/${id}`);
  }

  getPraticaById(id: string) {
    return this.getById(id);
  }

  search(
    page: number,
    size: number,
    praticaFilter?: Partial<PraticaSearchDTO>
  ): Observable<{ items: PraticaAggregator[]; total: number }> {
    let endpoint = praticaFilter?.inbox ? 'search-inbox' : 'search';
    delete praticaFilter?.inbox;

    let params = genHttpParams(praticaFilter);

    params = params.set('page', page).set('size', size);

    return this.http
      .get<{ datas: PraticaAggregator[]; total: number }>(
        `${this.aggregatorEndpoint}/${endpoint}`,
        { params }
      )
      .pipe(
        map((res) => {
          return {
            total: res.total,
            items: res.datas.map((el) => {
              let stipulaDate: moment.Moment | undefined = undefined;
              if (el.prodottoResponse?.[0]?.dataPrenotazioneStipula) {
                stipulaDate = moment(
                  el.prodottoResponse?.[0]?.dataPrenotazioneStipula
                );
              }

              let now = moment();
              let isStipulaDay = stipulaDate && now.isSame(stipulaDate, 'day');

              let isInStipula = false;

              let lastUpdated = moment(
                el.praticaResponse.dataCambioStatoPratica
              );

              let diff = now.diff(lastUpdated, 'days');

              let hasBeenUpdatedInTheLast3Days = diff <= 2;
              return {
                ...el,
                prodottoResponse: el.prodottoResponse || [],
                isDayOfStipula: isInStipula,
                hasBeenUpdatedInTheLast3Days,
                initials:
                  (el.user?.nome?.charAt(0) || '') +
                  (el.user?.cognome?.charAt(0) || ''),

                fullName: `${el.user?.nome || ''} ${el.user?.cognome || ''} `,
              };
            }),
          };
        }),
        catchError((err) => of({ items: [], total: 0 }))
      );
  }

  create(formData: {
    target: string;
    identifier: string;
    subscriber: string;
    tipoProdotto: ProductType | null;
    tipoPratica: string;
  }) {
    return this.http.post<{ process: string }>(this.processEndpoint, formData);
  }

  assignChannel(
    body: PraticaChannelConfigurationDialogReturnData<'assign-channel'>['data']['channelInfo'] & {
      subscriber: string;
    },
    praticaId: string,
    productType: ProductType
  ) {
    return this.http.post(
      `${this.istruttoriaEndpoint}${
        productType === 'FINANZIAMENTO CRESCIAGRI'
          ? '-cresciagri/start'
          : '/start'
      }`,
      {
        ...body,
        pratica: praticaId,
      }
    );
  }

  assignPratiche(userId: string, praticaId: string) {
    /*  let requests$ = praticheId.map((el) =>
      this.http.put(`${this.commandEndpoint}/${el}/${userId}`, {})
    );
    return forkJoin(requests$); */
    return this.http.put(`${this.commandEndpoint}/${praticaId}/${userId}`, {});
  }

  getCounters(
    praticaType: PraticaType = 'FIN',
    statiPratica: StatoPratica[] = []
  ) {
    const params = new HttpParams()
      .set('statusList', statiPratica.toString())
      .set('praticaTypes', praticaType);
    return this.http
      .get<Record<StatoPratica, number>>(
        `${this.queryEndpoint}/count-pratiche-by-status`,
        { params }
      )
      .pipe(
        map((res) =>
          typedEntries(res).map(([key, value]) => ({
            state: key,
            counter: value,
          }))
        )
      );
  }
}
